import { useState } from "react";
import { toast } from "react-toastify";
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";

// Calls
import {
  createSetupIntentRequest,
  setDefaultPaymentMethodRequest,
  deletePaymentMethodRequest
} from "../../../../api/calls/subscription.call";

// Billing UI
import {
  stripePromise,
  ELEMENTS_OPTIONS,
  StripeCardField,
  BillingModal,
  InlineError,
  Spinner,
  SavedCardRow
} from "./shared";
import { cardLabel } from "./icons";

const CheckRow = ({ checked, onChange, children }) => (
  <label className="checkrow">
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
    />
    <span>{children}</span>
  </label>
);

/* ---------- Add payment method (SetupIntent + Elements) ---------- */
const AddCardForm = ({ onClose, onSaved }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    const setupIntentResponse = await createSetupIntentRequest();
    if (setupIntentResponse.hasError) {
      setBusy(false);
      setError(setupIntentResponse.error.message);
      return;
    }
    const result = await stripe.confirmCardSetup(
      setupIntentResponse.clientSecret,
      { payment_method: { card: elements.getElement(CardElement) } }
    );
    if (result.error) {
      setBusy(false);
      setError(result.error.message);
      return;
    }
    if (setAsDefault) {
      const defaultResponse = await setDefaultPaymentMethodRequest(
        result.setupIntent.payment_method
      );
      if (defaultResponse.hasError) {
        toast.error(defaultResponse.error.message);
      }
    }
    toast.success("Card added.");
    onSaved();
    onClose();
  };

  return (
    <BillingModal
      title="Add payment method"
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={!stripe || !cardComplete || busy}
          >
            {busy ? (
              <>
                <Spinner /> Saving…
              </>
            ) : (
              "Save card"
            )}
          </button>
        </>
      }
    >
      <StripeCardField
        invalid={!!error}
        onChange={event => setCardComplete(event.complete)}
      />
      <div style={{ marginTop: 12 }}>
        <CheckRow checked={setAsDefault} onChange={setSetAsDefault}>
          Set as default payment method
        </CheckRow>
      </div>
      {error && <InlineError style={{ marginTop: 14 }}>{error}</InlineError>}
    </BillingModal>
  );
};

export const AddCardModal = props => (
  <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
    <AddCardForm {...props} />
  </Elements>
);

/* ---------- Remove payment method ---------- */
export const RemoveCardModal = ({ card, onClose, onRemoved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    const response = await deletePaymentMethodRequest(card.id);
    setBusy(false);
    if (response.hasError) {
      // e.g. the last card on an active subscription — BE blocks it.
      setError(response.error.message);
      return;
    }
    toast.success("Payment method removed.");
    onRemoved();
    onClose();
  };

  return (
    <BillingModal
      title={`Remove ${cardLabel(card)}?`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={remove} disabled={busy}>
            {busy ? (
              <>
                <Spinner /> Removing…
              </>
            ) : (
              "Remove card"
            )}
          </button>
        </>
      }
    >
      <p className="mdl-text">
        This payment method will be removed from your business. You can add it
        again later.
      </p>
      {error && <InlineError style={{ marginTop: 14 }}>{error}</InlineError>}
    </BillingModal>
  );
};

/* ---------- Change default payment method ---------- */
export const ChangeDefaultModal = ({ cards, onClose, onChanged }) => {
  const defaultCard = cards.find(card => card.isDefault) || cards[0] || {};
  const [sel, setSel] = useState(defaultCard.id);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const response = await setDefaultPaymentMethodRequest(sel);
    setBusy(false);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Default payment method updated.");
    onChanged();
    onClose();
  };

  return (
    <BillingModal
      title="Change default payment method"
      sub="Future charges will use the card you select."
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={busy || !sel || sel === defaultCard.id}
          >
            {busy ? (
              <>
                <Spinner /> Saving…
              </>
            ) : (
              "Save"
            )}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {cards.map(card => (
          <SavedCardRow
            key={card.id}
            card={card}
            selected={sel === card.id}
            onSelect={() => setSel(card.id)}
          />
        ))}
      </div>
    </BillingModal>
  );
};
